import { Space } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';
import { useCallback } from 'react';

import { useAppSelector, useCrossPopup } from 'shared/lib';
import type { TOffer } from 'shared/types/TRankingService';

import coins from '../../assets/coins.png';
import { IncomeConfirmationModalTexts } from '../../IncomeConfirmationModal.texts';
import {
  getEsiaApplicationBenefits,
  getEsiaButtonTitle,
  getIncomeConfirmationEsiaAuthUrl,
  getRegularApplicationBenefits,
  useIncomeConfirmationAnalytics,
} from '../../lib';
import { selectMplApplicationData } from '../../model';
import { ApplicationOptionCard } from '../ApplicationOptionCard';

import styles from './IncomeConfirmationModal.module.scss';

type TIncomeConfirmationModalProps = {
  visible: boolean;
  offer: TOffer;
  isSubmitting?: boolean;
  onClose: () => void;
  onSendRegularApplication: () => void | Promise<void>;
};

export const IncomeConfirmationModal: FC<TIncomeConfirmationModalProps> = ({
  visible,
  offer,
  isSubmitting = false,
  onClose,
  onSendRegularApplication,
}) => {
  const Popup = useCrossPopup();
  const { count: mplCount, logos: mplLogos } = useAppSelector(selectMplApplicationData);
  const { onClickOption } = useIncomeConfirmationAnalytics(offer, visible);
  const isMobile = useIsMobile();

  const onEsiaClick = useCallback(() => {
    onClickOption('esia');
    window.location.href = getIncomeConfirmationEsiaAuthUrl();
  }, [onClickOption]);

  const onRegularClick = useCallback(() => {
    onClickOption('regular');
    onSendRegularApplication();
  }, [onClickOption, onSendRegularApplication]);

  return (
    <Popup
      visible={visible}
      onClose={onClose}
      className={styles.popup}
      closable={false}
    >
      <Popup.Header
        className={styles.header}
        title={
          <Space
            direction="vertical"
            align="center"
          >
            {isMobile && (
              <img
                src={coins}
                alt="Монеты"
                width={240}
              />
            )}
            {IncomeConfirmationModalTexts.title}
          </Space>
        }
        subtitle={IncomeConfirmationModalTexts.subtitle}
      />
      <Popup.Content>
        <div className={styles.cards}>
          <ApplicationOptionCard
            category={offer.category}
            logos={[offer.logo]}
            title={IncomeConfirmationModalTexts.regularCardTitle}
            benefits={getRegularApplicationBenefits(offer.bankName)}
            buttonTitle={IncomeConfirmationModalTexts.sendRegularApplication}
            isSubmitting={isSubmitting}
            onButtonClick={onRegularClick}
          />
          <ApplicationOptionCard
            logos={mplLogos}
            title={IncomeConfirmationModalTexts.esiaCardTitle}
            benefits={getEsiaApplicationBenefits(offer.bankName, mplCount)}
            buttonTitle={getEsiaButtonTitle(mplCount)}
            isHighlighted
            onButtonClick={onEsiaClick}
          />
        </div>
      </Popup.Content>
    </Popup>
  );
};
